/**
 * "Download" on a report: the whole screen as one Excel workbook.
 *
 * One sheet for the headline cards, one per table, the period in every
 * heading and in the file name — "Control tower - Jul – Sept 2026.xlsx" —
 * so a month-end pack is a row of downloads rather than a row of renames.
 *
 * It reads the tables the screen is drawing rather than asking each report
 * for an exporter of its own. That keeps the workbook exactly the screen,
 * filters and sort included, and means a new report gets a download for
 * free. The server turns it into a real .xlsx with numbers as numbers.
 */

import { useRef, useState } from "react";
import { downloadFile } from "../lib/api2";
import { describePeriod, type Period } from "../lib/period";

interface Sheet {
  name: string;
  columns: string[];
  rows: (string | null)[][];
}

const text = (el: Element | null | undefined): string =>
  (el as HTMLElement | null)?.innerText?.replace(/\s+/g, " ").trim() ?? "";

/** A title as written, not as styled — CSS upper-cases some of them. The
 *  (i) beside a label explains it on screen and is not part of its name. */
const title = (el: Element): string => {
  const copy = el.cloneNode(true) as Element;
  copy.querySelectorAll(".info").forEach((i) => i.remove());
  return (copy.textContent ?? "").replace(/\s+/g, " ").trim();
};

/**
 * A header cell's label. Sheet headers also hold sort and filter controls,
 * so read the label's own text rather than everything drawn in the cell.
 */
const heading = (el: Element): string =>
  title(el.querySelector(".sheet__sort > span:first-child, .sheet__label") ?? el);

function sheetName(table: Element, index: number): string {
  // The nearest title above the table: walk back through what precedes it,
  // then through what precedes each of its containers, up to the page.
  // A tab bar counts: the table under "By day" is the by-day table.
  const titles = "h2, h3, .section-title";
  for (let el: Element | null = table; el && !el.classList.contains("page");
       el = el.parentElement) {
    for (let sib = el.previousElementSibling; sib; sib = sib.previousElementSibling) {
      const h = sib.querySelector('[role="tab"][aria-selected="true"]')
        ?? (sib.matches(titles) ? sib : [...sib.querySelectorAll(titles)].pop());
      if (h && title(h)) return title(h);
    }
  }
  return `Table ${index + 1}`;
}

export function collectSheets(page: Element): Sheet[] {
  const sheets: Sheet[] = [];

  const cards = [...page.querySelectorAll(".kpi")].filter(
    (k) => !k.classList.contains("skeleton")
  );
  if (cards.length) {
    sheets.push({
      name: "Headline",
      columns: ["Measure", "Value", "Change", "Note"],
      rows: cards.map((k) => [
        title(k.querySelector(".label") ?? k),
        text(k.querySelector(".kpi-value")),
        text(k.querySelector(".kpi__delta")) || null,
        text(k.querySelector(".kpi__sub")) || null,
      ]),
    });
  }

  [...page.querySelectorAll("table")]
    .filter((t) => !t.closest(".periodmenu"))
    .forEach((table, i) => {
      const columns = [...table.querySelectorAll("thead th")].map(heading);
      const rows = [...table.querySelectorAll("tbody tr")]
        .map((tr) => [...tr.querySelectorAll("td")].map((td) => text(td) || null))
        .filter((r) => r.length > 0);
      // An empty table is a screen saying "nothing here"; a blank sheet
      // would say it less clearly.
      if (!rows.length) return;
      sheets.push({
        name: sheetName(table, i),
        columns: columns.length ? columns : rows[0].map((_, j) => `Column ${j + 1}`),
        rows,
      });
    });

  return sheets;
}

export function ReportDownload({ title, period }: { title: string; period: Period }) {
  const ref = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    const page = ref.current?.closest(".page");
    if (!page) return;
    const sheets = collectSheets(page);
    if (!sheets.length) {
      setError("Nothing on screen to download yet.");
      return;
    }
    const label = describePeriod(period);
    setBusy(true);
    setError(null);
    try {
      await downloadFile("/api/reports/workbook", `${title} - ${label}.xlsx`, {
        method: "POST",
        body: { title, period: label, sheets },
      });
    } catch {
      setError("The download failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="btn btn--sm btn--auto btn--ghost reportdownload"
        onClick={download}
        disabled={busy}
        title="Every table on this screen, in one Excel workbook named for the period"
      >
        {busy ? "Preparing…" : "⤓ Download"}
      </button>
      {error && (
        <span className="periodpicker__range" role="alert">{error}</span>
      )}
    </>
  );
}
